import {
  DAY_TIME_VALUE,
  HOUR_TIME_VALUE,
  MINUTE_TIME_VALUE,
  MONTH_TIME_VALUE,
  SECOND_TIME_VALUE,
  YEAR_TIME_VALUE,
} from './consts';

export type DateFormat =
  //locale formats (affected by locale & timezone)
  | 'full'
  | 'full_datetime'
  | 'long'
  | 'fixed_long'
  | 'medium'
  | 'short'
  | 'short_datetime'
  | 'time'
  | 'prose'
  | 'relative'
  | 'duration'
  | 'input_date'
  //Technical formats (not affected by locale nor timezone)
  | 'iso'
  | 'iso_datetime'
  | 'iso_complete'
  | 'utc'
  // Charts specific formats
  | 'month' // yyyy-MM format
  | 'day' // yyyy-MM-dd format
  | 'year' // yyyy format
  //!TODO: deprecate these
  | 'iso_date' //duplicate of iso in react intl
  //duplicate of relative in react intl
  | 'eu_date'
  | 'us_date';

export function getReactIntlDateFormatOptions(
  format: DateFormat,
): Intl.DateTimeFormatOptions {
  const formatOptions: Record<DateFormat, Intl.DateTimeFormatOptions> = {
    // Pure ICU predefined styles
    short: { dateStyle: 'short' }, //3/15/24 , 15.03.24, 2024/03/15
    medium: { dateStyle: 'medium' }, //Feb 25, 2025,
    long: { dateStyle: 'long' }, //February 25, 2025
    full: { dateStyle: 'full' }, //Tuesday, February 25, 2025
    // Input-optimized formats (padded, consistent 4-digit year)
    input_date: {
      year: 'numeric', // 4-digit year for clarity
      month: '2-digit', // Padded month
      day: '2-digit', // Padded day
    }, // 01/15/2025 (US), 15.01.2025 (DE), 15/01/2025 (GB)
    // Clean ICU style combinations
    short_datetime: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, //1/15/2025, 02:30 PM
    full_datetime: {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, //Wednesday, January 15, 2025 at 02:30 PM
    time: {
      hour: '2-digit',
      minute: '2-digit',
    }, //"02:30 PM" (US) / "02:30" (EU)
    // New technical date formats
    month: {
      year: 'numeric',
      month: '2-digit',
    }, // yyyy-MM format: 2025-02
    day: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }, // yyyy-MM-dd format: 2025-02-25
    year: {
      year: 'numeric',
    }, // yyyy format: 2025
    //These are supported by React Intl, refer to custom formats in useFormatDate()
    duration: {},
    relative: {},
    prose: {},
    //not supported by React Intl, refer to technical formats in useFormatDate()
    utc: {},
    fixed_long: {},
    iso: {},
    iso_date: {},
    iso_datetime: {},
    iso_complete: {},
    //!DEPRECATED: These violate ICU locale-sensitivity rules
    eu_date: { day: '2-digit', month: '2-digit', year: 'numeric' }, //25/02/2025 with react 02/25/2025
    us_date: { month: '2-digit', day: '2-digit', year: 'numeric' }, //02/25/2025 with react 02/25/2025
  };

  return formatOptions[format];
}

export const getRelativeTimeOptions = (date: Date) => {
  const now = Date.now();
  const diffInSeconds = Math.round((date.getTime() - now) / 1000);
  const absSeconds = Math.abs(diffInSeconds);

  const units: {
    threshold: number;
    unit: Intl.RelativeTimeFormatUnit;
    divisor: number;
  }[] = [
    {
      threshold: YEAR_TIME_VALUE,
      unit: 'year',
      divisor: YEAR_TIME_VALUE,
    },
    {
      threshold: MONTH_TIME_VALUE,
      unit: 'month',
      divisor: MONTH_TIME_VALUE,
    },
    { threshold: DAY_TIME_VALUE, unit: 'day', divisor: DAY_TIME_VALUE },
    { threshold: HOUR_TIME_VALUE, unit: 'hour', divisor: HOUR_TIME_VALUE },
    {
      threshold: MINUTE_TIME_VALUE,
      unit: 'minute',
      divisor: MINUTE_TIME_VALUE,
    },
    { threshold: 0, unit: 'second', divisor: SECOND_TIME_VALUE },
  ];

  for (const { threshold, unit, divisor } of units) {
    if (absSeconds >= threshold) {
      const value = Math.round(diffInSeconds / divisor);
      if (value !== 0) {
        return { value, unit: unit as Intl.RelativeTimeFormatUnit };
      }
    }
  }

  return {
    value: 0,
    unit: 'second' as Intl.RelativeTimeFormatUnit,
  };
};
